const CompareMvp = (() => {
  const MAX_ITEMS = 4;
  const STORAGE_KEY = 'theme1202_compare_mvp_v1';
  const SELECTORS = {
    add: '[data-compare-add]',
    specs: 'script[data-compare-specs]',
    productCard: '[data-product-item]',
  };

  const LOCALE = (() => {
    try {
      const el = document.getElementById('cc-locale');
      if (el && el.textContent) return JSON.parse(el.textContent);
    } catch {}
    return {};
  })();

  const _l = (key, fallback) => LOCALE[key] || fallback || key;

  const CART_SVG = `
    <svg width="20" height="20" viewBox="0 0 21 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d="M16.731 19.393a1.4 1.4 0 1 1 0-2.8 1.4 1.4 0 0 1 0 2.8Zm-9.74 0a1.4 1.4 0 1 1 0-2.8 1.4 1.4 0 0 1 0 2.8ZM2.534 1.9H1A.9.9 0 0 1 1 .1h2.277a.9.9 0 0 1 .883.73l.469 2.422h15.14a.9.9 0 0 1 .876 1.105l-2.203 9.445a.9.9 0 0 1-.876.695H5.712a.9.9 0 0 1-.884-.729L2.534 1.9Zm16.1 3.152H4.977l1.477 7.645h10.397l1.783-7.645Z" fill="currentColor" fill-rule="evenodd" />
    </svg>
  `;

  const state = {
    group: null,
    items: [],
    trayEl: null,
    tableEl: null,
    uniqueToggleEl: null,
    headingEl: null,
    backdropEl: null,
    toastEl: null,
    isOpen: false,
    mqMobile: null,
  };

  const getGroup = () => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    if (parts[0] === 'collections' && parts[1]) return `collection:${parts[1]}`;
    return 'global';
  };

  const readStorage = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      return parsed;
    } catch {
      return {};
    }
  };

  const writeStorage = (payload) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {}
  };

  const loadItems = () => {
    const storage = readStorage();
    const group = getGroup();
    const items = Array.isArray(storage[group]) ? storage[group] : [];
    state.group = group;
    state.items = items.filter((x) => x && typeof x === 'object' && x.handle);
  };

  const saveItems = () => {
    const storage = readStorage();
    storage[state.group] = state.items;
    writeStorage(storage);
  };

  const normalizeText = (value) => {
    if (value == null) return '';
    return String(value).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  };

  const parseBoolean = (value) => {
    const normalized = normalizeText(value).toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
    return null;
  };

  // CUSTOM:AQARA-HK START id=normalize-variant-key reason=handle-traditional-vs-simplified-chinese-character-mismatch
  const normalizeVariantSpecKey = (optionName, optionValue) => {
    // Map traditional Chinese to simplified Chinese for consistent key matching
    // This handles cases where swatch_option.name may use traditional Chinese
    // but metafield variants use simplified Chinese
    const charMap = {
      '顏': '颜',  // 顏色 → 颜色
      '鎖': '锁',  // 鎖 → 锁
      '鍵': '钥',  // 鍵 → 钥
      // Add more mappings as needed
    };
    let normalized = optionName;
    for (const [trad, simp] of Object.entries(charMap)) {
      normalized = normalized.split(trad).join(simp);
    }
    return normalized + '::' + optionValue;
  };
  // CUSTOM:AQARA-HK END id=normalize-variant-key

  const normalizeSpecs = (specs) => {
    if (!Array.isArray(specs)) return [];
    return specs
      .map((row) => {
        if (!row || typeof row !== 'object') return null;
        const name = normalizeText(row.name ?? row.label ?? row.key ?? '');
        const value = normalizeText(row.title ?? row.value ?? row.val ?? row.text ?? row.content ?? '');
        if (!name) return null;
        return { section: normalizeText(row.section) || '', name, value };
      })
      .filter(Boolean);
  };

  const parseSpecsFromCard = (cardEl) => {
    const scriptEl = cardEl.querySelector(SELECTORS.specs);
    if (!scriptEl) return [];
    try {
      const parsed = JSON.parse(scriptEl.textContent || '[]');
      // Support both direct array and object with fallback/variants structure
      if (Array.isArray(parsed)) {
        return normalizeSpecs(parsed);
      } else if (parsed && typeof parsed === 'object' && Array.isArray(parsed.fallback)) {
        return normalizeSpecs(parsed.fallback);
      }
      return [];
    } catch {
      return [];
    }
  };

  const upsertItem = (item) => {
    const idx = state.items.findIndex((x) => x.handle === item.handle);
    if (idx >= 0) {
      state.items.splice(idx, 1);
      return;
    }
    if (state.items.length >= MAX_ITEMS) {
      showToast(_l('limit_reached', 'You can compare up to 4 products.'));
      return;
    }
    state.items.push(item);
  };

  const removeItem = (handle) => {
    state.items = state.items.filter((x) => x.handle !== handle);
  };

  const clearItems = () => {
    state.items = [];
  };

  const isMobile = () => {
    if (state.mqMobile) return state.mqMobile.matches;
    return window.matchMedia('(max-width: 620px)').matches;
  };

  const showToast = (message) => {
    if (!state.toastEl) {
      const el = document.createElement('div');
      el.className = 'cc-toast';
      document.body.appendChild(el);
      state.toastEl = el;
    }
    const toast = state.toastEl;
    toast.textContent = message;
    toast.classList.add('on');
    clearTimeout(toast.__timer);
    toast.__timer = setTimeout(() => {
      toast.classList.remove('on');
    }, 3000);
  };

  const syncCards = () => {
    const handles = new Set(state.items.map((x) => x.handle));
    document.querySelectorAll('.product-card--root[data-product-item]').forEach((card) => {
      const handle = card.getAttribute('data-handle');
      if (!handle) return;
      const on = handles.has(handle);
      if (on) {
        card.setAttribute('data-compare-selected', '');
      } else {
        card.removeAttribute('data-compare-selected');
      }
      const btn = card.querySelector('.cc-compare-button');
      if (btn) {
        btn.classList.toggle('cc-compare-button--on', on);
        btn.textContent = on ? _l('selected', 'Selected') : _l('select', 'Compare');
      }
    });
  };

  const ensureUi = () => {
    if (state.trayEl) return;

    const backdrop = document.createElement('div');
    backdrop.className = 'cc-backdrop';
    backdrop.setAttribute('aria-hidden', 'true');
    backdrop.addEventListener('click', () => closeSheet());
    document.body.appendChild(backdrop);
    state.backdropEl = backdrop;

    const tray = document.createElement('div');
    tray.className = 'cc-tray';
    tray.hidden = true;
    tray.dataset.ccState = 'collapsed';
    tray.innerHTML = `
      <div class="cc-sheet" role="region" aria-label="${_l('title', 'Compare')}">
        <div class="cc-sheet__collapsed">
          <div class="cc-sheet__bar">
            <div class="cc-sheet__title">${_l('title', 'Compare')}<span class="cc-sheet__title-cnt" data-cc-count></span></div>
            <div class="cc-sheet__slots" data-cc-slots></div>
            <div class="cc-sheet__actions">
              <button class="cc-sheet__btn cc-sheet__btn--text" type="button" data-cc-cancel>${_l('cancel', 'Cancel')}</button>
              <button class="cc-sheet__btn cc-sheet__btn--primary" type="button" data-cc-open>${_l('compare_now', 'Compare Now')}</button>
            </div>
          </div>
        </div>

        <div class="cc-sheet__expanded" aria-hidden="true">
          <div class="cc-sheet__header">
            <div class="cc-sheet__heading" data-cc-heading>${_l('title', 'Compare')}</div>
            <div class="cc-sheet__header-actions">
              <label class="cc-toggle">
                <input type="checkbox" data-cc-unique>
                <span>${_l('unique_only', 'Unique specs only')}</span>
              </label>
              <button class="cc-sheet__fullclose" type="button" data-cc-fullclose aria-label="${_l('close', 'Close')}">${_l('close', 'Close')}</button>
            </div>
          </div>
          <div class="cc-sheet__body">
            <div class="cc-sheet__table" data-cc-table></div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(tray);

    state.trayEl = tray;
    state.tableEl = tray.querySelector('[data-cc-table]');
    state.uniqueToggleEl = tray.querySelector('[data-cc-unique]');
    state.headingEl = tray.querySelector('[data-cc-heading]');

    tray.querySelector('[data-cc-open]').addEventListener('click', () => openSheet());
    tray.querySelector('[data-cc-cancel]').addEventListener('click', () => {
      clearItems();
      saveItems();
      renderTray();
      syncCards();
      closeSheet();
    });
    tray.querySelector('[data-cc-fullclose]').addEventListener('click', () => closeSheet());
    state.uniqueToggleEl.addEventListener('change', () => renderTable());

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!state.isOpen) return;
      closeSheet();
    });

    state.mqMobile = window.matchMedia('(max-width: 620px)');
    state.mqMobile.addEventListener('change', () => {
      if (state.isOpen) {
        buildPanelColumns();
        syncPanelRows();
      }
    });
  };

  const renderTray = () => {
    ensureUi();
    
    // CUSTOM:AQARA-HK START id=sync-latest-swatch-on-tray reason=update-tray-with-latest-product-card-swatch-values
    // 在渲染訓已前，同步產品列表中的最新色塊值
    state.items.forEach(function (item) {
      var cardEl = document.querySelector('.product-card--root[data-handle="' + item.handle + '"]');
      if (!cardEl) return;
      
      var latestSwatchValue = cardEl.dataset.currentSwatchValue;
      var latestVariantId = cardEl.dataset.currentVariantId;
      
      // 如果訓項目有最新的色塊值，且與當前不同，則更新
      if (latestSwatchValue && latestSwatchValue !== item.selectedSwatchValue) {
        item.selectedSwatchValue = latestSwatchValue;
        if (latestVariantId) item.variantId = Number(latestVariantId);
      }
    });
    // CUSTOM:AQARA-HK END id=sync-latest-swatch-on-tray
    
    const tray = state.trayEl;
    const slotsEl = tray.querySelector('[data-cc-slots]');
    const openBtn = tray.querySelector('[data-cc-open]');

    let slots = Array.from(slotsEl.children);
    while (slots.length > MAX_ITEMS) { slots.pop().remove(); }
    while (slots.length < MAX_ITEMS) {
      const slot = document.createElement('div');
      slot.className = 'cc-tile';
      slotsEl.appendChild(slot);
      slots.push(slot);
    }

    for (let i = 0; i < MAX_ITEMS; i += 1) {
      const item = state.items[i] || null;
      const slot = slots[i];
      const currentHandle = slot.dataset.ccHandle || '';

      if (item && item.handle !== currentHandle) {
        slot.className = 'cc-tile';
        slot.dataset.ccHandle = item.handle;
        const img = item.image ? `<img class="cc-tile__img" src="${item.image}" alt="">` : '';
        slot.innerHTML = `
          <div class="cc-tile__filled">
            <div class="cc-tile__media">${img}</div>
            <div class="cc-tile__title"></div>
            <button class="cc-tile__remove" type="button" aria-label="${_l('remove', 'Remove')}">&times;</button>
          </div>
        `;
        slot.querySelector('.cc-tile__title').textContent = item.title || item.handle;
        slot.querySelector('.cc-tile__remove').addEventListener('click', () => {
          removeItem(item.handle);
          saveItems();
          renderTray();
          syncCards();
          renderTable();
        });
      } else if (item) {
        const titleEl = slot.querySelector('.cc-tile__title');
        if (titleEl && titleEl.textContent !== (item.title || item.handle)) {
          titleEl.textContent = item.title || item.handle;
        }
        // 更新圖片（同一產品切換色塊時圖片可能變化）
        const trayImg = slot.querySelector('.cc-tile__img');
        if (trayImg && item.image && trayImg.src !== item.image) {
          trayImg.src = item.image;
        }
      } else if (currentHandle !== '' || !slot.classList.contains('cc-tile--empty')) {
        slot.className = 'cc-tile cc-tile--empty';
        slot.dataset.ccHandle = '';
        slot.innerHTML = `
          <span class="cc-tile__empty-text">${_l('select_from_above', 'Select from above')}</span>
        `;
      }
    }

    const hasItems = state.items.length > 0;
    tray.hidden = !hasItems;
    openBtn.disabled = state.items.length < 2;

    const countEl = tray.querySelector('[data-cc-count]');
    if (countEl) countEl.textContent = state.items.length;

    document.documentElement.classList.toggle('cc-tray-open', hasItems);
  };

  const updateCartCountDom = (count) => {
    const n = Number(count);
    if (!Number.isFinite(n)) return;
    document.querySelectorAll('.header--cart').forEach((el) => {
      el.setAttribute('data-cart-count', String(n));
    });
    const viewport = document.querySelector('.layout--viewport');
    if (viewport) viewport.setAttribute('data-cart-empty', n > 0 ? 'false' : 'true');
  };

  const fetchCartCount = async () => {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart_fetch_failed');
    const cart = await res.json();
    if (cart && typeof cart.item_count === 'number') updateCartCountDom(cart.item_count);
  };

  const refreshCartDrawer = async () => {
    const container = document.querySelector('.drawer--container[data-view="cart-drawer"]');
    if (!container) return;
    const existingCartRoot = container.querySelector('cart-element.cart--root');
    const existingForm = existingCartRoot ? existingCartRoot.querySelector('.cart--form') : null;
    const res = await fetch('/?sections=cart-drawer', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart_drawer_fetch_failed');
    const sections = await res.json();
    const html = sections && typeof sections['cart-drawer'] === 'string' ? sections['cart-drawer'] : '';
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sectionEl = doc.querySelector('#shopify-section-cart-drawer');
    const incomingRoot = sectionEl ? sectionEl : doc.body;
    const newCartRoot = incomingRoot.querySelector('cart-element.cart--root');
    const newForm = newCartRoot ? newCartRoot.querySelector('.cart--form') : null;

    if (existingCartRoot && existingForm && newForm) {
      existingForm.innerHTML = newForm.innerHTML;
    } else if (newCartRoot) {
      container.innerHTML = newCartRoot.outerHTML;
    } else {
      container.innerHTML = sectionEl ? sectionEl.innerHTML : html;
    }

    const cartRoot = container.querySelector('cart-element.cart--root');
    if (!cartRoot) return;

    cartRoot.querySelectorAll('[data-transition-item]').forEach((el) => {
      if (el.getAttribute('data-transition-trigger') === 'hover') return;
      const type = el.getAttribute('data-transition-type') || '';
      if (type.endsWith('-out')) return;
      el.setAttribute('data-transition-active', 'true');
    });

    const form = cartRoot.querySelector('.cart--form');
    const summary = cartRoot.querySelector('.cart--summary');
    if (form && summary) {
      const getSafeAreaInsetBottom = () => {
        const probe = document.createElement('div');
        probe.style.cssText =
          'position:fixed;left:0;bottom:0;height:0;padding-bottom:env(safe-area-inset-bottom);visibility:hidden;pointer-events:none;';
        document.body.appendChild(probe);
        const inset = Number.parseFloat(window.getComputedStyle(probe).paddingBottom) || 0;
        probe.remove();
        return inset;
      };

      const safeAreaBottom = getSafeAreaInsetBottom();
      const canMeasure = () => summary.getClientRects().length > 0 && (summary.offsetHeight || summary.getBoundingClientRect().height) > 0;
      const updateSummaryHeight = () => {
        if (!canMeasure()) return false;
        const base = summary.offsetHeight || Math.ceil(summary.getBoundingClientRect().height);
        const extra = 16 + safeAreaBottom;
        const px = Math.ceil(base + extra);
        cartRoot.style.setProperty('--summary-height', `${px}px`);
        form.style.setProperty('padding-bottom', `${px}px`);
        form.style.setProperty('scroll-padding-bottom', `${px}px`);
        return true;
      };

      const applyWithRetries = () => {
        const ok = updateSummaryHeight();
        if (!ok) return false;
        window.requestAnimationFrame(updateSummaryHeight);
        window.setTimeout(updateSummaryHeight, 250);
        return true;
      };

      if (!applyWithRetries()) {
        const existing = cartRoot.__ccDrawerGutterObserver;
        if (!existing) {
          const rightSide = document.querySelector('.drawer--side[data-side="right"]');
          const mo = new MutationObserver(() => {
            if (applyWithRetries()) {
              mo.disconnect();
              cartRoot.__ccDrawerGutterObserver = null;
            }
          });
          if (rightSide) {
            mo.observe(rightSide, { attributes: true, attributeFilter: ['aria-expanded'] });
          } else {
            mo.observe(document.documentElement, { attributes: true, subtree: true });
          }
          cartRoot.__ccDrawerGutterObserver = mo;
          window.setTimeout(() => {
            if (cartRoot.__ccDrawerGutterObserver !== mo) return;
            if (applyWithRetries()) {
              mo.disconnect();
              cartRoot.__ccDrawerGutterObserver = null;
              return;
            }
            mo.disconnect();
            cartRoot.__ccDrawerGutterObserver = null;
          }, 3000);
        }
      }
    }

    if (form) form.scrollTop = 0;
  };

  const addVariantToCart = async (variantId, quantity = 1) => {
    const id = Number(variantId);
    if (!Number.isFinite(id) || id <= 0) throw new Error('invalid_variant');
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id, quantity }),
    });
    if (!res.ok) throw new Error('cart_add_failed');
    await res.json();
    await Promise.allSettled([fetchCartCount(), refreshCartDrawer()]);
  };

  const openSheet = () => {
    ensureUi();
    if (state.items.length < 2) return;
    state.isOpen = true;
    state.trayEl.dataset.ccState = 'expanded';
    state.trayEl.querySelector('.cc-sheet__expanded').setAttribute('aria-hidden', 'false');
    state.backdropEl.classList.add('on');
    state.backdropEl.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cc-sheet-open');
    document.documentElement.style.overflow = 'hidden';
    try { renderTable(); } catch (e) { console.error('CompareMvp: renderTable failed', e); }
  };

  const closeSheet = () => {
    if (!state.trayEl) return;
    state.isOpen = false;
    state.trayEl.dataset.ccState = 'collapsed';
    state.trayEl.querySelector('.cc-sheet__expanded').setAttribute('aria-hidden', 'true');
    state.backdropEl.classList.remove('on');
    state.backdropEl.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cc-sheet-open');
    document.documentElement.style.overflow = '';
  };

  const buildSpecMatrix = () => {
    const sections = [];
    const sectionOrder = [];
    const sectionMap = new Map();
    const keyToName = new Map();
    const valuesByKey = new Map();

    const makeKey = (name) => normalizeText(name).toLowerCase();

    for (const item of state.items) {
      for (const row of item.specs || []) {
        const key = makeKey(row.name);
        if (!key) continue;
        if (!keyToName.has(key)) {
          keyToName.set(key, row.name);
          const section = row.section || '';
          if (!sectionMap.has(section)) {
            sectionMap.set(section, []);
            sectionOrder.push(section);
          }
          sectionMap.get(section).push(key);
        }
      }
    }

    for (const secName of sectionOrder) {
      sections.push({
        name: secName,
        keys: sectionMap.get(secName),
      });
    }

    for (const [, keys] of sectionMap) {
      for (const key of keys) {
        valuesByKey.set(key, state.items.map(() => '\u2014'));
      }
    }

    state.items.forEach((item, idx) => {
      const map = new Map();
      for (const row of item.specs || []) {
        const key = makeKey(row.name);
        if (!key) continue;
        map.set(key, row.value || '\u2014');
      }
      for (const [, keys] of sectionMap) {
        for (const key of keys) {
          const current = valuesByKey.get(key);
          current[idx] = map.get(key) || '\u2014';
        }
      }
    });

    return { sections, keyToName, valuesByKey };
  };

  const buildPanelColumns = () => {
    const cprow = state.tableEl.querySelector('.cc-table');
    if (!cprow) { renderTable(); return; }
    const oldHeadRow = cprow.firstChild;
    const reusableCols = new Map();
    if (oldHeadRow && oldHeadRow.classList && oldHeadRow.classList.contains('cc-row--head')) {
      oldHeadRow.querySelectorAll('.cc-cell--product[data-p]').forEach((col) => {
        reusableCols.set(col.dataset.p, col);
      });
      oldHeadRow.remove();
    }

    const handles = state.items.map((x) => x.handle);
    const mobile = isMobile();
    const slotsToShow = mobile
      ? Math.max(2, Math.min(MAX_ITEMS, handles.length))
      : Math.max(2, Math.min(MAX_ITEMS, handles.length + 1));

    const headRow = document.createElement('div');
    headRow.className = 'cc-row cc-row--head';
    headRow.style.setProperty('--cols', String(slotsToShow));

    const headCell = document.createElement('div');
    headCell.className = 'cc-cell cc-cell--name';
    headCell.textContent = _l('selected_models', '\u5df2\u9078\u985e\u578b');
    headRow.appendChild(headCell);

    for (let i = 0; i < slotsToShow; i++) {
      const handle = handles[i];
      let col;
      if (handle && reusableCols.has(handle)) {
        col = reusableCols.get(handle);
        reusableCols.delete(handle);
      } else if (handle) {
        const p = state.items.find((x) => x.handle === handle);
        col = p ? createProductColumn(handle, p) : createEmptyColumn();
      } else {
        col = createEmptyColumn();
      }
      headRow.appendChild(col);
    }

    cprow.prepend(headRow);
  };

  const syncPanelRows = () => {
    const handles = state.items.map((x) => x.handle);
    const table = state.tableEl;
    const rows = table.querySelectorAll('.cc-row:not(.cc-row--head):not(.cc-row--section)');

    rows.forEach((row) => {
      const cells = Array.from(row.querySelectorAll('.cc-cell--value:not(.cc-cell--empty)'));
      cells.forEach((c) => { c.style.display = 'none'; });

      const shown = [];
      handles.forEach((handle) => {
        const cell = cells.find((c) => c.dataset.p === handle);
        if (cell) {
          cell.style.display = '';
          shown.push(cell);
        }
      });

      row.querySelectorAll('.cc-cell--value.cc-cell--placeholder').forEach((p) => p.remove());

      const slotsToShow = row.style.getPropertyValue('--cols') || state.items.length;
      const need = Math.max(0, parseInt(slotsToShow) - shown.length);
      for (let i = 0; i < need; i++) {
        const ph = document.createElement('div');
        ph.className = 'cc-cell cc-cell--value cc-cell--placeholder';
        ph.textContent = '\u2014';
        row.appendChild(ph);
      }

      const values = shown.map((c) => normalizeText(c.textContent).toLowerCase());
      const allSame = values.length > 1 && values.every((v) => v === values[0]);
      row.toggleAttribute('hidden', state.uniqueToggleEl.checked && allSame);
    });
  };

  const createProductColumn = (handle, p) => {
    const col = document.createElement('div');
    col.className = 'cc-cell cc-cell--value cc-cell--product';
    col.dataset.p = handle;

    const x = document.createElement('button');
    x.type = 'button';
    x.className = 'cc-product__remove';
    x.setAttribute('aria-label', _l('remove', 'Remove'));
    x.innerHTML = '&times;';
    x.addEventListener('click', () => {
      removeItem(handle);
      saveItems();
      renderTray();
      syncCards();
      if (state.items.length < 2) { closeSheet(); return; }
      renderTable();
    });
    col.appendChild(x);

    const nm = document.createElement('div');
    nm.className = 'cc-product__name';
    nm.textContent = p.title || p.handle;
    col.appendChild(nm);

    const img = document.createElement('div');
    img.className = 'cc-product__imgwrap';
    if (p.image) {
      const imgEl = document.createElement('img');
      imgEl.className = 'cc-product__img';
      imgEl.src = p.image;
      imgEl.alt = '';
      img.appendChild(imgEl);
    }
    col.appendChild(img);

    // CUSTOM:AQARA-HK START id=compare-panel-swatches-render reason=render-swatch-buttons-in-compare-panel-column
    if (p.swatchHtml) {
      var swatchWrap = document.createElement('div');
      swatchWrap.className = 'cc-product__swatches';
      swatchWrap.innerHTML = p.swatchHtml;
      // Make IDs unique to avoid DOM conflicts with original product cards
      swatchWrap.querySelectorAll('input[type="radio"]').forEach(function (input, idx) {
        var originalId = input.id;
        var uid = 'cc-sw-' + handle + '-' + idx;
        input.id = uid;
        input.name = 'cc-swatch-' + handle;
        var parentLabel = input.closest('label');
        if (parentLabel && parentLabel.getAttribute('for') === originalId) {
          parentLabel.setAttribute('for', uid);
        }
        // Restore selected state from persisted value
        if (p.selectedSwatchValue && input.value === p.selectedSwatchValue) {
          input.checked = true;
        }
      });
      col.appendChild(swatchWrap);

      // CUSTOM:AQARA-HK START id=compare-panel-swatch-init reason=trigger-initial-swatch-change-for-spec-sync
      // 色塊 DOM 注入後，觸發初始化以同步規格、圖片、variant
      setTimeout(function () {
        var checkedInput = swatchWrap.querySelector('input[type="radio"]:checked');
        if (checkedInput && p.selectedSwatchValue) {
          var event = new Event('change', { bubbles: true });
          checkedInput.dispatchEvent(event);
        }
      }, 0);
      // CUSTOM:AQARA-HK END id=compare-panel-swatch-init
    }
    // CUSTOM:AQARA-HK END id=compare-panel-swatches-render

    if (p.tagline) {
      const tagline = document.createElement('div');
      tagline.className = 'cc-product__tagline';
      tagline.textContent = p.tagline;
      col.appendChild(tagline);
    }

    if (p.price) {
      const price = document.createElement('div');
      price.className = 'cc-product__price';
      price.innerHTML = '<b>HK' + p.price + '</b>';
      col.appendChild(price);
    }

    const cta = document.createElement('div');
    cta.className = 'cc-product__cta';

    const buy = document.createElement('button');
    buy.type = 'button';
    buy.className = 'cc-product__cta-btn';
    buy.textContent = _l('add_to_cart', 'Add to Cart');
    const isUnavailable = p.available === false;
    buy.disabled = isUnavailable || !p.variantId;
    buy.addEventListener('click', async () => {
      if (buy.disabled) return;
      buy.disabled = true;
      buy.dataset.loading = 'true';
      try { await addVariantToCart(p.variantId, 1); } catch {} finally {
        buy.removeAttribute('data-loading');
        buy.disabled = isUnavailable || !p.variantId;
      }
    });
    cta.appendChild(buy);

    if (p.url) {
      const view = document.createElement('a');
      view.className = 'cc-product__view-link';
      view.href = p.url;
      view.textContent = _l('view_details', 'View Details');
      cta.appendChild(view);
    }
    col.appendChild(cta);

    return col;
  };

  const createEmptyColumn = () => {
    const col = document.createElement('div');
    col.className = 'cc-cell cc-cell--value cc-cell--empty';
    const ico = document.createElement('div');
    ico.className = 'cc-product__empty-ico';
    ico.textContent = '+';
    const label = document.createElement('div');
    label.className = 'cc-product__empty-label';
    label.textContent = _l('add_another', 'Add another');
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'cc-product__empty-add';
    add.textContent = _l('back_to_select', 'Back to select');
    add.addEventListener('click', () => {
      closeSheet();
      const grid = document.getElementById('collection-product-grid');
      const target = grid || document.querySelector('.product-card--root');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    col.appendChild(ico);
    col.appendChild(label);
    col.appendChild(add);
    return col;
  };

  const renderTable = () => {
    if (!state.isOpen) return;
    
    // CUSTOM:AQARA-HK START id=sync-latest-swatch-on-render reason=update-compare-items-with-latest-product-card-swatch-values
    // 在渲染前，同步產品列表中的最新色塊值到對比項目
    state.items.forEach(function (item) {
      var cardEl = document.querySelector('.product-card--root[data-handle="' + item.handle + '"]');
      if (!cardEl) return;
      
      // 檢查卡片上是否有最新的色塊選中值
      var latestSwatchValue = cardEl.dataset.currentSwatchValue;
      var latestVariantId = cardEl.dataset.currentVariantId;
      
      // 如果卡片上有最新值，且與對比項目不同，則更新
      if (latestSwatchValue && latestSwatchValue !== item.selectedSwatchValue) {
        item.selectedSwatchValue = latestSwatchValue;
        if (latestVariantId) item.variantId = Number(latestVariantId);
      }
    });
    // CUSTOM:AQARA-HK END id=sync-latest-swatch-on-render
    
    const uniqueOnly = !!state.uniqueToggleEl.checked;
    const { sections, keyToName, valuesByKey } = buildSpecMatrix();

    const headingFmt = _l('heading', 'Compare ({count})');
    state.headingEl.textContent = headingFmt.replace('{count}', String(state.items.length));

    const handles = state.items.map((x) => x.handle);
    const mobile = isMobile();
    const slotsToShow = mobile
      ? Math.max(2, Math.min(MAX_ITEMS, handles.length))
      : Math.max(2, Math.min(MAX_ITEMS, handles.length + 1));

    const reusableCols = new Map();
    const oldTable = state.tableEl.querySelector('.cc-table');
    if (oldTable) {
      oldTable.querySelectorAll('.cc-row--head .cc-cell--product[data-p]').forEach((col) => {
        reusableCols.set(col.dataset.p, col);
      });
    }

    state.tableEl.textContent = '';

    const table = document.createElement('div');
    table.className = 'cc-table';

    const headRow = document.createElement('div');
    headRow.className = 'cc-row cc-row--head';
    headRow.style.setProperty('--cols', String(slotsToShow));

    const headCell = document.createElement('div');
    headCell.className = 'cc-cell cc-cell--name';
    headCell.textContent = _l('selected_models', '\u5df2\u9078\u985e\u578b');
    headRow.appendChild(headCell);

    for (let i = 0; i < slotsToShow; i++) {
      const handle = handles[i];
      let col;
      if (handle && reusableCols.has(handle)) {
        col = reusableCols.get(handle);
        reusableCols.delete(handle);
        // 更新復用列的圖片和色塊（避免因 renderTable 復用舊 DOM 導致顯示陳舊）
        const p = state.items.find((x) => x.handle === handle);
        if (p) {
          var imgEl = col.querySelector('.cc-product__img');
          if (imgEl && p.image && imgEl.src !== p.image) {
            imgEl.src = p.image;
          }
          if (p.selectedSwatchValue) {
            col.querySelectorAll('.cc-product__swatches .swatch--input').forEach(function(input) {
              input.checked = (input.value === p.selectedSwatchValue);
            });
          }
        }
      } else if (handle) {
        const p = state.items.find((x) => x.handle === handle);
        col = p ? createProductColumn(handle, p) : createEmptyColumn();
      } else {
        col = createEmptyColumn();
      }
      headRow.appendChild(col);
    }
    table.appendChild(headRow);

    for (const section of sections) {
      let visibleKeys = section.keys;
      if (uniqueOnly) {
        visibleKeys = section.keys.filter((key) => {
          const values = valuesByKey.get(key) || [];
          const normalized = values.map((v) => normalizeText(v).toLowerCase());
          const first = normalized[0] || '';
          return !normalized.every((v) => v === first);
        });
      }

      if (section.name && visibleKeys.length > 0) {
        const secRow = document.createElement('div');
        secRow.className = 'cc-row cc-row--section';
        const secCell = document.createElement('div');
        secCell.className = 'cc-cell cc-cell--section-name';
        const secName = document.createElement('span');
        secName.className = 'cc-cell__section-label';
        secName.textContent = section.name;
        const secCount = document.createElement('span');
        secCount.className = 'cc-cell__section-cnt';
        secCount.textContent = visibleKeys.length + ' \u9805';
        secCell.append(secName, secCount);
        secRow.appendChild(secCell);
        table.appendChild(secRow);
      }

      for (const key of visibleKeys) {
        const values = valuesByKey.get(key) || [];

        const rowEl = document.createElement('div');
        rowEl.className = 'cc-row';
        rowEl.style.setProperty('--cols', String(slotsToShow));

        const nameCell = document.createElement('div');
        nameCell.className = 'cc-cell cc-cell--name';
        nameCell.textContent = keyToName.get(key) || '';
        rowEl.appendChild(nameCell);

        values.forEach((v, idx) => {
          if (idx >= handles.length) return;
          const cell = document.createElement('div');
          cell.className = 'cc-cell cc-cell--value';
          cell.dataset.p = handles[idx];
          cell.textContent = normalizeText(v) || '\u2014';
          rowEl.appendChild(cell);
        });

        for (let i = values.length; i < slotsToShow; i++) {
          const ph = document.createElement('div');
          ph.className = 'cc-cell cc-cell--value cc-cell--placeholder';
          ph.textContent = '\u2014';
          rowEl.appendChild(ph);
        }

        table.appendChild(rowEl);
      }
    }

    state.tableEl.appendChild(table);
  };

  const handleAddClick = (triggerEl) => {
    const cardEl = triggerEl.closest(SELECTORS.productCard);
    if (!cardEl) return;

    const handle = normalizeText(triggerEl.getAttribute('data-compare-handle'));
    if (!handle) return;

    const variantId = normalizeText(triggerEl.getAttribute('data-compare-variant-id'));
    const available = parseBoolean(triggerEl.getAttribute('data-compare-available'));

    // CUSTOM:AQARA-HK START id=enhance-swatch-capture reason=read-current-swatch-value-and-variant-from-card
    let swatchHtml = '';
    let swatchOptionPosition = null;
    let swatchOptionName = null;
    let variantSpecs = null;
    let selectedSwatchValue = null;  // ← 新增：記錄當前選中值
    let currentVariantId = null;      // ← 新增：記錄當前 variant
    const swatchContainer = cardEl.querySelector(
      '.product-card--swatches--horizontal-view .swatches--container'
    );
    if (swatchContainer) {
      swatchHtml = swatchContainer.outerHTML;
      const pos = cardEl.getAttribute('data-swatch-option-position');
      if (pos) swatchOptionPosition = parseInt(pos);
      const name = cardEl.getAttribute('data-swatch-option-name');
      if (name) swatchOptionName = name;
    
      // 讀取卡片上追蹤的當前色塊值
      selectedSwatchValue = cardEl.dataset.currentSwatchValue || null;
      currentVariantId = cardEl.dataset.currentVariantId || null;
    
      // 解析 variant-level specs 從預加載的 JSON
      const vSpecsEl = cardEl.querySelector('script[data-compare-variant-specs]');
      if (vSpecsEl) {
        try {
          const compareData = JSON.parse(vSpecsEl.textContent || '{}');
          // Handle both full metafield object and variants-only object
          if (compareData.variants) {
            variantSpecs = compareData.variants;
          } else if (typeof compareData === 'object' && compareData !== null && !Array.isArray(compareData)) {
            // If it's a plain object with keys like "顔色::銀", treat it as variants
            variantSpecs = compareData;
          }
        } catch (e) {}
      }
    }
    // CUSTOM:AQARA-HK END id=enhance-swatch-capture

    const item = {
      handle,
      title: normalizeText(triggerEl.getAttribute('data-compare-title')) || handle,
      url: normalizeText(triggerEl.getAttribute('data-compare-url')) || '',
      // CUSTOM:AQARA-HK START id=use-current-variant-image reason=prefer-card-dataset-image-when-variant-selected-for-re-add-after-remove
      image: (currentVariantId && cardEl.dataset.currentVariantImage)
        ? cardEl.dataset.currentVariantImage.replace(/\.([^./]+)$/, '_160x.$1')
        : (normalizeText(triggerEl.getAttribute('data-compare-image')) || ''),
      // CUSTOM:AQARA-HK END id=use-current-variant-image
      specs: parseSpecsFromCard(cardEl),
      variantId: currentVariantId ? Number(currentVariantId) : (variantId ? Number(variantId) : null),
      // CUSTOM:AQARA-HK START id=use-current-variant-available reason=use-card-dataset-available-for-re-add-after-remove
      available: currentVariantId
        ? parseBoolean(cardEl.dataset.currentVariantAvailable)
        : available,
      // CUSTOM:AQARA-HK END id=use-current-variant-available
      price: normalizeText(triggerEl.getAttribute('data-compare-price')) || '',
      tagline: normalizeText(triggerEl.getAttribute('data-compare-tagline')) || '',
      swatchHtml,
      swatchOptionPosition,
      swatchOptionName,
      variantSpecs,
      selectedSwatchValue,  // ← 新增：保存選中值
    };

    // CUSTOM:AQARA-HK START id=merge-variant-specs-on-add reason=merge-variant-specific-specs-when-re-adding-after-remove
    // 若當前已選擇特定 variant（色塊），合併 variant 專屬規格到 item.specs
    mergeVariantSpecsIntoItem(item);
    // CUSTOM:AQARA-HK END id=merge-variant-specs-on-add

    upsertItem(item);
    saveItems();
    syncCards();
    renderTray();
    renderTable();
  };

  // CUSTOM:AQARA-HK START id=merge-variant-specs-helper reason=shared-specs-merge-logic-for-card-swatch-sync-and-re-add-after-remove
  const mergeVariantSpecsIntoItem = (item) => {
    if (!item.variantSpecs || !item.selectedSwatchValue) return;
    
    var newSpecs = null;
    var namespacedKey = item.swatchOptionName 
      ? normalizeVariantSpecKey(item.swatchOptionName, item.selectedSwatchValue) 
      : null;
    
    if (namespacedKey && item.variantSpecs.hasOwnProperty(namespacedKey) && item.variantSpecs[namespacedKey]) {
      newSpecs = normalizeSpecs(item.variantSpecs[namespacedKey]);
    } else if (item.variantSpecs.hasOwnProperty(item.selectedSwatchValue) && item.variantSpecs[item.selectedSwatchValue]) {
      newSpecs = normalizeSpecs(item.variantSpecs[item.selectedSwatchValue]);
    } else {
      for (var key in item.variantSpecs) {
        if (item.variantSpecs.hasOwnProperty(key) && key.indexOf('::') > -1 && key.endsWith('::' + item.selectedSwatchValue)) {
          newSpecs = normalizeSpecs(item.variantSpecs[key]);
          break;
        }
      }
    }

    if (!newSpecs) return;

    if (item.specs && item.specs.length > 0) {
      var variantSpecMap = {};
      newSpecs.forEach(function(spec) {
        var key = normalizeText(spec.name).toLowerCase();
        variantSpecMap[key] = spec;
      });
      var mergedSpecs = item.specs.map(function(fallbackSpec) {
        var key = normalizeText(fallbackSpec.name).toLowerCase();
        return variantSpecMap[key] || fallbackSpec;
      });
      newSpecs.forEach(function(variantSpec) {
        var key = normalizeText(variantSpec.name).toLowerCase();
        if (!item.specs.some(function(s) { return normalizeText(s.name).toLowerCase() === key; })) {
          mergedSpecs.push(variantSpec);
        }
      });
      item.specs = mergedSpecs;
    } else {
      item.specs = newSpecs;
    }
  };
  // CUSTOM:AQARA-HK END id=merge-variant-specs-helper

  // CUSTOM:AQARA-HK START id=track-card-swatch-selection reason=track-current-swatch-value-on-product-card-for-compare-capture
  const updateCompareItemFromCardSwatch = (handle, selectedValue, matchingVariant) => {
    // 檢查該產品是否已在對比中，如果是則更新
    var itemIdx = state.items.findIndex(function (x) { return x.handle === handle; });
    if (itemIdx < 0) return; // 產品未在對比中，無需更新

    var item = state.items[itemIdx];
    
    // 1. 更新色塊值和 variant 基本信息
    item.selectedSwatchValue = selectedValue;
    if (matchingVariant) {
      item.variantId = matchingVariant.id;
      item.available = matchingVariant.available;
      
      // 2. 更新圖片
      if (matchingVariant.featured_image) {
        item.image = matchingVariant.featured_image.src
          .replace(/\.([^./]+)$/, '_160x.$1');
      }
    }

    // 3. 合併 variant 專屬規格（使用共享函數）
    mergeVariantSpecsIntoItem(item);

    // 4. 保存並重渲
    saveItems();
    // 更新 tray（renderTray 的 else-if 分支會處理同 handle 圖片更新）
    renderTray();
    if (state.isOpen) {
      // renderTable 內部已處理復用列的圖片和色塊更新
      renderTable();
    }
  };

  const trackCardSwatchChanges = () => {
    document.addEventListener('change', function (event) {
      // 只追蹤產品卡片上的色塊（不包括對比面板）
      var input = event.target.closest('.product-card--root .swatch--input');
      if (!input) return;

      var cardEl = input.closest('[data-product-item]');
      if (!cardEl) return;

      var selectedValue = input.value;
      if (!selectedValue) return;

      // 在卡片元素上儲存當前選中的色塊值
      cardEl.dataset.currentSwatchValue = selectedValue;

      // 同時追蹤對應的 variant ID
      var handle = cardEl.getAttribute('data-handle');
      if (handle) {
        var optionPosition = parseInt(cardEl.getAttribute('data-swatch-option-position')) || 1;
        var optionIndex = optionPosition - 1;
        
        fetch('/products/' + handle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (productData) {
            if (!productData) return;
            var matchingVariant = null;  // 保存完整 variant 對象
            for (var i = 0; i < productData.variants.length; i++) {
              var v = productData.variants[i];
              if (v.options && v.options.length > optionIndex &&
                  v.options[optionIndex].toLowerCase() === selectedValue.toLowerCase()) {
                matchingVariant = v;
                cardEl.dataset.currentVariantId = v.id;
                // CUSTOM:AQARA-HK START id=store-variant-meta-on-card-data reason=preserve-variant-image-available-for-re-add-to-compare
                cardEl.dataset.currentVariantImage = (v.featured_image && v.featured_image.src) ? v.featured_image.src : '';
                cardEl.dataset.currentVariantAvailable = String(v.available);
                // CUSTOM:AQARA-HK END id=store-variant-meta-on-card-data
                break;
              }
            }
            // CUSTOM:AQARA-HK START id=sync-compare-on-card-swatch-change reason=update-compare-panel-when-card-swatch-changes
            // 傳遞完整 variant 數據以供全面同步（圖片、規格、可售狀態）
            updateCompareItemFromCardSwatch(handle, selectedValue, matchingVariant);
            // CUSTOM:AQARA-HK END id=sync-compare-on-card-swatch-change
          })
          .catch(function () {});
      }
    });
  };
  // CUSTOM:AQARA-HK END id=track-card-swatch-selection

  const bind = () => {
    // 追蹤產品卡片上的色塊變更
    trackCardSwatchChanges();
    
    document.addEventListener('click', (event) => {
      const el = event.target.closest(SELECTORS.add);
      if (!el) return;
      event.preventDefault();
      loadItems();
      ensureUi();
      handleAddClick(el);
    });

    // CUSTOM:AQARA-HK START id=compare-swatch-change-handler reason=handle-swatch-change-in-compare-panel-with-specs-refresh
    document.addEventListener('change', function (event) {
      var input = event.target.closest('.cc-product__swatches .swatch--input');
      if (!input) return;
      var col = input.closest('.cc-cell--product');
      if (!col) return;
      var handle = col.dataset.p;
      if (!handle) return;

      var idx = state.items.findIndex(function (x) { return x.handle === handle; });
      if (idx < 0) return;
      var item = state.items[idx];

      var selectedValue = input.value;
      var optionIndex = (item.swatchOptionPosition || 1) - 1;

      // Save selected swatch value for persistence across table rebuilds
      item.selectedSwatchValue = selectedValue;

      // 同步卡片 dataset，防止 renderTable/renderTray 的同步區塊反向覆蓋面板選擇
      // CUSTOM:AQARA-HK START id=panel-to-card-swatch-sync reason=prevent-sync-blocks-from-overwriting-panel-swatch-selection
      var cardEl = document.querySelector('.product-card--root[data-handle="' + handle + '"]');
      if (cardEl) {
        cardEl.dataset.currentSwatchValue = selectedValue;
      }
      // CUSTOM:AQARA-HK END id=panel-to-card-swatch-sync

      // Check for variant-level specs
      var newSpecs = null;
      if (item.variantSpecs) {
        // Build namespaced key using option name and selected value: "option_name::value"
        // Apply character normalization to handle traditional vs simplified Chinese
        var namespacedKey = item.swatchOptionName ? normalizeVariantSpecKey(item.swatchOptionName, selectedValue) : null;
        
        // Try normalized namespaced key first
        if (namespacedKey && item.variantSpecs.hasOwnProperty(namespacedKey) && item.variantSpecs[namespacedKey]) {
          newSpecs = normalizeSpecs(item.variantSpecs[namespacedKey]);
        } else if (item.variantSpecs.hasOwnProperty(selectedValue) && item.variantSpecs[selectedValue]) {
          // Fallback to direct key match
          newSpecs = normalizeSpecs(item.variantSpecs[selectedValue]);
        } else {
          // Last resort: search for matching namespaced key
          for (var key in item.variantSpecs) {
            if (key.indexOf('::') > -1 && key.endsWith('::' + selectedValue)) {
              newSpecs = normalizeSpecs(item.variantSpecs[key]);
              break;
            }
          }
        }
      }

      // Fetch product JSON to find matching variant
      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (productData) {
          if (!productData) return;

          // Find matching variant by option value
          var matchingVariant = null;
          for (var i = 0; i < productData.variants.length; i++) {
            var v = productData.variants[i];
            if (v.options && v.options.length > optionIndex &&
                v.options[optionIndex].toLowerCase() === selectedValue.toLowerCase()) {
              matchingVariant = v;
              break;
            }
          }
          if (!matchingVariant) return;

          // 同步卡片 variantId，與選中值保持一致
          // CUSTOM:AQARA-HK START id=panel-to-card-variantid-sync reason=sync-variant-id-to-card-dataset
          var cardEl = document.querySelector('.product-card--root[data-handle="' + handle + '"]');
          if (cardEl) {
            cardEl.dataset.currentVariantId = matchingVariant.id;
          }
          // CUSTOM:AQARA-HK END id=panel-to-card-variantid-sync

          // Update image
          var imgEl = col.querySelector('.cc-product__img');
          if (imgEl && matchingVariant.featured_image) {
            imgEl.src = matchingVariant.featured_image.src.replace(/\.([^./]+)$/, '_160x.$1');
          }

          // Update item data
          item.variantId = matchingVariant.id;
          item.available = matchingVariant.available;
          item.image = matchingVariant.featured_image
            ? matchingVariant.featured_image.src.replace(/\.([^./]+)$/, '_160x.$1')
            : item.image;

          // Merge variant-level specs with fallback specs
          // variant specs should OVERRIDE fallback specs for matching names
          if (newSpecs && item.specs && item.specs.length > 0) {
            // Create a map of variant specs by name for quick lookup
            var variantSpecMap = {};
            newSpecs.forEach(function(spec) {
              var key = normalizeText(spec.name).toLowerCase();
              variantSpecMap[key] = spec;
            });
            // Merge: start with fallback, override with variant specs where name matches
            var mergedSpecs = item.specs.map(function(fallbackSpec) {
              var key = normalizeText(fallbackSpec.name).toLowerCase();
              if (variantSpecMap[key]) {
                // Override with variant spec
                return variantSpecMap[key];
              }
              // Keep fallback spec
              return fallbackSpec;
            });
            // Add any variant specs that aren't in fallback
            newSpecs.forEach(function(variantSpec) {
              var key = normalizeText(variantSpec.name).toLowerCase();
              if (!item.specs.some(function(s) { return normalizeText(s.name).toLowerCase() === key; })) {
                mergedSpecs.push(variantSpec);
              }
            });
            item.specs = mergedSpecs;
          } else if (newSpecs) {
            // If no fallback exists, use variant specs directly
            item.specs = newSpecs;
          }

          // Update buy button
          var buyBtn = col.querySelector('.cc-product__cta-btn');
          if (buyBtn) {
            var newBuy = buyBtn.cloneNode(true);
            newBuy.disabled = !matchingVariant.available;
            newBuy.addEventListener('click', (function (vid) {
              return function () {
                if (newBuy.disabled) return;
                newBuy.disabled = true;
                newBuy.dataset.loading = 'true';
                addVariantToCart(vid, 1).finally(function () {
                  newBuy.removeAttribute('data-loading');
                  newBuy.disabled = !matchingVariant.available;
                });
              };
            })(matchingVariant.id));
            buyBtn.parentNode.replaceChild(newBuy, buyBtn);
          }

          saveItems();

          // Rebuild table if specs changed
          if (newSpecs) {
            renderTable();
          }
        })
        .catch(function () {});
    });
    // CUSTOM:AQARA-HK END id=compare-swatch-change-handler
  };

  const bindCollectionAtc = () => {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.product-card--atc[data-atc-variant-id]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      btn.disabled = true;
      btn.dataset.loading = 'true';
      try {
        await addVariantToCart(Number(btn.dataset.atcVariantId), 1);
      } catch {}
      btn.removeAttribute('data-loading');
      btn.disabled = false;
    });
  };

  const init = () => {
    if (document.body && document.body.classList.contains('template--product')) return;
    loadItems();
    bind();
    bindCollectionAtc();
    syncCards();
    if (state.items.length > 0) renderTray();
  };

  return { init };
})();
window.CompareMvp = CompareMvp;

window.addEventListener('DOMContentLoaded', () => {
  CompareMvp.init();
});
